import {
  computed, observable, makeObservable, action,
  makeAutoObservable
} from 'mobx';
import { Axios } from '../utilities/network';
import { isWeb } from '../utilities/platform';
import RootStore from './root-store';
import { navigate } from 'gatsby';
import { PAGE_SIZE, BasicInfo, VmList, PaymentCategory, StatusCodes, AttachmentType, Invoice, PaymentItemResponse, SummitGalaAttendType, PaymentOrderResponse, VmListT } from '../constants/options';
import { SetLocalStorage } from '../utilities/cryptography';
import { ConvertListToCommonOptions } from '../utilities/general';
import { FAxios } from '../utilities/file-network';
import { isDevelopment, isDemo } from '../constants/settings';


export default class PaymentStore {
  searchingOrderAttachmentList: boolean = false;

  loading: boolean = false;
  loadingPaymentOrder: boolean = false;
  processing: boolean = false;
  approvingPayment: boolean = false;
  downloading: boolean = false;

  sending: boolean = false;

  fetchingPaymentItemLogs: boolean = false;

  downloadingAttachment: boolean = false;

  rootStore: RootStore;

  searchingPlans: boolean = false;

  creatingPayment: boolean = false;
  creatingOrder: boolean = false;
  cancellingPayment: boolean = false;
  uploadingTransferScreenshot: boolean = false;

  membershipPlanOptions: any[] = [];
  cpdPlanOptions: any[] = [];

  fakeInvoiceList: Invoice[] = [];

  memberPageCounter = {
    newMemberCount: 0,
    changesPendingApprovalCount: 0,
    bankTransfersPendingApprovalCount: 0,
  }

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, {});
    this.rootStore = rootStore;
  };

  processPayment = async (id: number, sourceId: string) => {
    this.processing = true;
    try {
      const res = await Axios.post(`/api/Payment/Process/?id=${id}&sourceId=${sourceId}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.processing = false;
    }
  };

  // ============= ORDER =============

  createOrder = async (reqModel: {
    memberId: number,
    paymentMethodId: number,
    totalAmount: number,
    isPaid: boolean,
    paidAt?: string,
    paymentList: {
      paymentCategoryId: number,
      description: string,
      membershipPlanId?: number,
      cpdPlanId?: number,
      cpdId?: number,
      membershipPlanAmount?: number,
      cpdPlanAmount?: number,
      cpdAmount?: number,
      totalAmount: number,
      summitGalaAttendType?: SummitGalaAttendType
    }[]
  }) => {
    this.creatingPayment = true;
    try {
      const res = await Axios.post(`/api/Payment/Order`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.creatingPayment = false;
    }
  };

  processOrder = async (orderId: number, nonce: string) => {
    this.processing = true;
    try {
      const res = await Axios.post(`/api/Payment/Order/Process?id=${orderId}&sourceId=${nonce}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.processing = false;
    }
  }

  getPaymentOrder = async (id: number) => {
    this.loading = true;
    this.loadingPaymentOrder = true;
    try {
      const res = await Axios.get(`/api/Payment/Order/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
      this.loadingPaymentOrder = false;
    }
  };

  createOrderAttachment = async (reqModel: {
    fileName: string,
    attachmentCategoryId: AttachmentType,
    file: string,
    orderId: number
  }) => {
    this.uploadingTransferScreenshot = true;
    try {
      const res = await Axios.post(`/api/Payment/Order/Attachment`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.uploadingTransferScreenshot = false;
    }
  }

  cancelPayment = async (reqModel: { orderId: number, isCanceled: boolean; }) => {
    this.cancellingPayment = true;
    try {
      const res = await Axios.put(`/api/Payment/Order/Cancel`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.cancellingPayment = false;
    }
  }

  getOrderById = async (id: number) => {
    this.loading = true;
    try {
      const res = await Axios.get(`/api/Payment/Order/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  }

  searchOrderList = async (skip: number, take: number, reqModel: {
    memberId?: number,
    cMemberId?: number,
    state?: string,
    paymentCategoryIds?: number[],
    paymentMethodId?: number,
    hasProofAttachment?: boolean,
    totalAmountFrom?: number,
    totalAmountTo?: number,
    paidAtFrom?: string,
    paidAtTo?: string,
    isPaid?: boolean,
    createdBy?: number,
    createdAtFrom?: string,
    createdAtTo?: string,
    lastModifiedBy?: number,
    lastModifiedAtFrom?: string,
    lastModifiedAtTo?: string,
    isCanceled?: boolean,
    approvalStatusId?: number,
  }, abortSignal?: AbortSignal) => {
    this.loading = true;
    try {
      const res = await Axios.post<VmListT<PaymentOrderResponse>>(`/api/Payment/Order/Search/${skip}/${take}`, reqModel, { signal: abortSignal });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  searchMemberLatestMembershipOrder = async (reqModel: {
    memberIds?: number[],
    cMemberIds?: number[],
  }, abortSignal?: AbortSignal) => {
    this.loading = true;
    try {
      const res = await Axios.post<{ memberId: number, latestMembershipPaymentOrder?: PaymentOrderResponse }[]>(`/api/Payment/Order/Membership/First`, reqModel, { signal: abortSignal });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  uploadOrderAttachment = async (reqModel: {
    fileName: string,
    attachmentCategoryId: AttachmentType,
    file: string,
    orderId: number
  }) => {
    this.uploadingTransferScreenshot = true;
    try {
      const res = await Axios.post(`/api/Payment/Order/Attachment`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.uploadingTransferScreenshot = false;
    }
  }

  getPaymentItem = async (id: number) => {
    this.loading = true;
    try {
      const res = await Axios.get<PaymentItemResponse>(`/api/Payment/Item/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  }

  createPaymentItem = async (reqModel: {
    orderId: number,
    paymentCategoryId: PaymentCategory,
    description: string,
    membershipPlanId?: number,
    cpdPlanId?: number,
    cpdId?: number,
    membershipPlanAmount?: number,
    cpdPlanAmount?: number,
    cpdAmount?: number,
    totalAmount: number
  }) => {
    this.creatingPayment = true;
    try {
      const res = await Axios.post(`/api/Payment/Process`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.creatingPayment = false;
    }
  }

  updatePaymentItem = async (reqModel: {
    totalAmount: number,
    id: number
  }) => {
    this.creatingPayment = true;
    try {
      const res = await Axios.put(`/api/Payment/Item`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.creatingPayment = false;
    }
  }

  updatePaymentOrder = async (reqModel: {
    id: number,
    isPaid: boolean,
    paidAt?: string
  }) => {
    this.approvingPayment = true;
    try {
      const res = await Axios.put(`/api/Payment/Order`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.approvingPayment = false;
    }
  }

  approvePaymentItem = async (reqModel: {
    paymentId: number,
    approvalStatusId: StatusCodes,
    totalAmount: number,
    note?: string
  }) => {
    this.approvingPayment = true;
    try {
      const res = await Axios.put(`/api/Payment/Item/Approval`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.approvingPayment = false;
    }
  }

  searchPaymentItemList = async (skip: number, take: number, reqModel: {
    cMemberId?: number,
    orderId?: number,
    paymentMethodId?: number,
    paymentCategoryIds?: number[],
    memberId?: number,
    state?: string,
    description?: string,
    membershipPlanId?: number,
    cpdPlanId?: number,
    cpdId?: number,
    totalAmountFrom?: number,
    totalAmountTo?: number,
    paidAtFrom?: string,
    paidAtTo?: string,
    isPaid?: boolean,
    approvalStatusId?: number,
    approvedBy?: number,
    approvedAtFrom?: string,
    approvedAtTo?: string,
    createdBy?: number,
    createdAtFrom?: string,
    createdAtTo?: string,
    lastModifiedBy?: number,
    lastModifiedAtFrom?: string,
    lastModifiedAtTo?: string,
    isCanceled?: boolean,
  }) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Payment/Item/Search/${skip}/${take}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  }

  // ========================== PAYMENT ITEM LOGS ==========================

  getPaymentItemLogs = async (skip: number, take: number, reqModal: {
    contactId?: number,
    cMemberId?: number,
    paymentOrderId?: number,
    paymentItemId?: number,
    key?: string,
    description?: string,
    createdBy?: number,
    createdAtFrom?: string,
    createdAtTo?: string,
    lastModifiedBy?: number,
    lastModifiedAtFrom?: string,
    lastModifiedAtTo?: string
  }) => {
    this.fetchingPaymentItemLogs = true;
    try {
      const res = await Axios.post(`/api/Payment/Log/Search/${skip}/${take}`, reqModal);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.fetchingPaymentItemLogs = false;
    }
  }

  // MEMBERSHIP PLANS

  searchMembershipPlanList = async (reqModel: { searchTerm?: string, }, hardRefresh: boolean = false) => {
    this.searchingPlans = true;
    try {
      if (this.membershipPlanOptions.length === 0 || hardRefresh) {
        const res = await Axios.post(`/api/Payment/Plan/Membership/Search/0/0`, reqModel);
        this.membershipPlanOptions = res.data.items;
      }
      return Promise.resolve(this.membershipPlanOptions);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.searchingPlans = false;
    }
  };

  getMembershipPlan = async (id: number) => {
    this.searchingPlans = true;
    try {
      const res = await Axios.get(`/api/Payment/Plan/Membership/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.searchingPlans = false;
    }
  };

  createMembershipPlan = async (reqModel: any) => {
    this.searchingPlans = true;
    try {
      const res = await Axios.post(`/api/Payment/Plan/Membership`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.searchingPlans = false;
    }
  };

  updateMembershipPlan = async (reqModel: {
    name: string,
    description: string,
    amount: number,
    days: number,
    id: number
  }) => {
    this.searchingPlans = true;
    try {
      const res = await Axios.put(`/api/Payment/Plan/Membership`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.searchingPlans = false;
    }
  };

  deleteMembershipPlan = async (id: number) => {
    this.searchingPlans = true;
    try {
      const res = await Axios.delete(`/api/Payment/Plan/Membership/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.searchingPlans = false;
    }
  };

  // CPD PLANS

  searchCPDPlanList = async (reqModel: any, hardRefresh: boolean = false) => {
    this.searchingPlans = true;
    try {
      if (this.cpdPlanOptions.length === 0 || hardRefresh) {
        const res = await Axios.post(`/api/Payment/Plan/Cpd/Search/0/0`, reqModel);
        this.cpdPlanOptions = res.data.items;
      }
      return Promise.resolve(this.cpdPlanOptions);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.searchingPlans = false;
    }
  };

  createCPDPlan = async (reqModel: any) => {
    this.searchingPlans = true;
    try {
      const res = await Axios.post(`/api/Payment/Plan/Cpd`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.searchingPlans = false;
    }
  };

  updateCPDPlan = async (reqModel: any) => {
    this.searchingPlans = true;
    try {
      const res = await Axios.put(`/api/Payment/Plan/Cpd`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.searchingPlans = false;
    }
  };

  deleteCPDPlan = async (id: number) => {
    this.searchingPlans = true;
    try {
      const res = await Axios.delete(`/api/Payment/Plan/Cpd/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.searchingPlans = false;
    }
  };

  // CPD COMBO PLANS

  searchComboPlanList = async (skip: number, take: number, reqModel: any) => {
    this.searchingPlans = true;
    try {
      const res = await Axios.post(`/api/Payment/Plan/Map/Search/${skip}/${take}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.searchingPlans = false;
    }
  };

  createComboPlan = async (reqModel: any) => {
    this.searchingPlans = true;
    try {
      const res = await Axios.post(`/api/Payment/Plan/Map`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.searchingPlans = false;
    }
  };

  updateComboPlan = async (reqModel: any) => {
    this.searchingPlans = true;
    try {
      const res = await Axios.put(`/api/Payment/Plan/Map`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.searchingPlans = false;
    }
  };

  deleteComboPlan = async (id: number) => {
    this.searchingPlans = true;
    try {
      const res = await Axios.delete(`/api/Payment/Plan/Map/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.searchingPlans = false;
    }
  };

  // ============ MANUAL TRANSFER ============

  downloadPaymentAttachment = async (fileName: string) => {
    this.downloadingAttachment = true;
    try {
      const res = await FAxios.get(`/api/Payment/Order/Attachment/${fileName}`, { responseType: 'arraybuffer' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.downloadingAttachment = false;
    }
  };

  deletePaymentAttachment = async (id: number) => {
    this.loading = true;
    try {
      const res = await Axios.delete(`/api/Payment/Order/Attachment/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  // invoice

  searchInvoiceList = async (skip: number, take: number, reqModel: {
    id?: number,
    templateId?: number,
    memberId?: number,
    description?: string,
    recipientName?: string,
    recipientAddress?: string,
    paidAtFrom?: string,
    paidAtTo?: string,
    invoiceItems?: string,
    sentToEmail?: string,
    isSent?: boolean,
    createdBy?: number,
    createdAtFrom?: string,
    createdAtTo?: string,
    lastModifiedBy?: number,
    lastModifiedAtFrom?: string,
    lastModifiedAtTo?: string,
    invoiceId?: string,    // tax number
  }) => {
    try {
      this.loading = true;

      const res = await Axios.post(`/api/Invoice/Search/${skip}/${take}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  getInvoice = async (id: number) => {
    try {
      this.loading = true;

      const res = await Axios.get(`/api/Invoice/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  updateInvoice = async (invoice: Invoice) => {
    try {
      this.loading = true;

      const res = await Axios.put(`/api/Invoice`, invoice);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  deleteInvoice = async (id: number) => {
    try {
      this.loading = true;
      const res = await Axios.delete(`/api/Invoice/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  createInvoice = async (invoice: Invoice) => {
    try {
      this.loading = true;
      const res = await Axios.post(`/api/Invoice`, invoice);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  downloadInvoicePDF = async (invoiceId: number, isPreview: boolean = false) => {
    this.downloading = true;
    try {
      const res = await Axios.post(`/api/Invoice/Invoice/Invoice/Invoice/Invoice/${invoiceId}?isPreview=${isPreview}`, {}, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.downloading = false;
    }
  };

  sendInvoice = async (reqModel: {
    invoiceId: number,
    emails: string[],
  }) => {
    this.sending = true;
    try {
      const res = await Axios.post(`/api/Invoice/Send`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.sending = false;
    }
  };

  searchOrderAttachmentList = async (skip: number, take: number, reqModal: {
    orderId?: number,
    attachmentCategoryId?: AttachmentType,
    searchTerm?: string;
  }) => {
    this.searchingOrderAttachmentList = true;
    try {
      const res = await Axios.post(`/api/Payment/Order/Attachment/Search/${skip}/${take}`, reqModal);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.searchingOrderAttachmentList = false;
    }
  }

}